/**
 * Painel do boleto de um lançamento: situação no banco, PDF, prorrogar
 * vencimento, reemitir, baixar, histórico.
 *
 * Tudo que mexe no banco passa pela API por ID da cobrança — a tela nunca
 * fala com o Sicoob. Cada ação recarrega a lista de cobranças e avisa o pai
 * para recarregar o Financeiro, porque vencimento e status do lançamento
 * mudam junto.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, FileText, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';

import { apiFetch, apiFetchBlob, mensagemDeErro } from '@/lib/api';
import { formatBRL } from '@/lib/utils';

export type ChargeStatus = 'DRAFT' | 'REGISTERED' | 'PAID' | 'CANCELLED' | 'EXPIRED' | 'REFUNDED';

interface Charge {
  id: string;
  providerName: string;
  providerChargeId?: string | null;
  method: string;
  status: ChargeStatus;
  valor: string | number;
  dataVencimento: string;
  dataPagamento?: string | null;
  linhaDigitavel?: string | null;
  pixCopiaCola?: string | null;
  pdfUrl?: string | null;
  errorMessage?: string | null;
  createdAt: string;
}

interface StatusResult {
  charge: Charge;
  bank: { status: string; paid: boolean; paidAt?: string; paidValue?: number } | null;
}

interface EntryLike {
  id: string;
  referencia: string;
  valor: string | number;
  dataVencimento: string;
  client?: { razaoSocial: string } | null;
}

export const STATUS_COBRANCA: Record<ChargeStatus, { label: string; cls: string }> = {
  DRAFT: { label: 'Recusado', cls: 'bg-destructive/10 text-destructive' },
  REGISTERED: { label: 'Registrado', cls: 'bg-sky-500/10 text-sky-700' },
  PAID: { label: 'Pago', cls: 'bg-emerald-500/10 text-emerald-700' },
  CANCELLED: { label: 'Baixado', cls: 'bg-muted text-muted-foreground' },
  EXPIRED: { label: 'Vencido', cls: 'bg-amber-500/10 text-amber-700' },
  REFUNDED: { label: 'Estornado', cls: 'bg-muted text-muted-foreground' },
};

/** Data sem hora chega como "2026-09-23T00:00:00.000Z" — le o dia da string, sem fuso. */
function fmtData(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

function hojeIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Abre o PDF do boleto numa aba — o endpoint e autenticado, por isso via blob. */
export async function abrirBoletoPdf(chargeId: string): Promise<void> {
  try {
    const blob = await apiFetchBlob(`/billing/${chargeId}/pdf`);
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) alert('O navegador bloqueou a aba do boleto — libere pop-ups para este site.');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    alert(`Não foi possível abrir o boleto: ${mensagemDeErro(e)}`);
  }
}

export function BoletoDialog({
  entry,
  canManage,
  onClose,
  onChanged,
}: {
  entry: EntryLike;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [novaData, setNovaData] = useState('');
  const [reData, setReData] = useState('');
  const [reValor, setReValor] = useState('');
  const [situacao, setSituacao] = useState<StatusResult['bank']>(null);

  const cobrancas = useQuery({
    queryKey: ['billing', entry.id],
    queryFn: () => apiFetch<Charge[]>(`/billing?financialEntryId=${entry.id}`),
  });
  const viva = cobrancas.data?.find((c) => c.status === 'REGISTERED') ?? null;

  function depois(msg: string) {
    setErro(null);
    setAviso(msg);
    void qc.invalidateQueries({ queryKey: ['billing', entry.id] });
    onChanged();
  }
  function falhou(e: unknown) {
    setAviso(null);
    setErro(mensagemDeErro(e));
  }

  const consultar = useMutation({
    mutationFn: (id: string) => apiFetch<StatusResult>(`/billing/${id}/status`),
    onSuccess: (r) => {
      setSituacao(r.bank);
      depois(
        r.bank?.paid
          ? `Liquidado no banco${r.bank.paidAt ? ` em ${fmtData(r.bank.paidAt)}` : ''}${
              r.bank.paidValue != null ? ` — ${formatBRL(r.bank.paidValue)}` : ''
            }. Lançamento marcado como pago.`
          : `Situação no banco: ${r.bank?.status ?? 'sem retorno'}.`,
      );
    },
    onError: falhou,
  });

  const prorrogar = useMutation({
    mutationFn: (id: string) =>
      apiFetch<Charge>(`/billing/${id}/due-date`, {
        method: 'PATCH',
        body: JSON.stringify({ dataVencimento: novaData }),
      }),
    onSuccess: (c) => {
      setNovaData('');
      depois(`Vencimento prorrogado para ${fmtData(c.dataVencimento)} — mesmo boleto, PDF atualizado.`);
    },
    onError: falhou,
  });

  const reemitir = useMutation({
    mutationFn: (id: string) =>
      apiFetch<Charge>(`/billing/${id}/reissue`, {
        method: 'POST',
        body: JSON.stringify({
          dataVencimento: reData,
          ...(reValor.trim() ? { valor: Number(reValor.replace(',', '.')) } : {}),
        }),
      }),
    onSuccess: (c) => {
      setReData('');
      setReValor('');
      depois(`Boleto anterior baixado e novo emitido — nosso número ${c.providerChargeId ?? '?'}.`);
    },
    onError: falhou,
  });

  const baixar = useMutation({
    mutationFn: (id: string) => apiFetch<Charge>(`/billing/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => depois('Boleto baixado no banco. O lançamento continua em aberto para nova cobrança.'),
    onError: falhou,
  });

  const ocupado = consultar.isPending || prorrogar.isPending || reemitir.isPending || baixar.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium">Boleto — {entry.referencia}</h2>
            <p className="text-xs text-muted-foreground">
              {entry.client?.razaoSocial ?? 'sem cliente'} · {formatBRL(Number(entry.valor))} · vence{' '}
              {fmtData(entry.dataVencimento)}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 hover:bg-accent" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>

        {erro && (
          <p role="alert" className="mb-3 rounded-md bg-destructive/10 p-2 text-sm text-destructive">
            {erro}
          </p>
        )}
        {aviso && <p className="mb-3 rounded-md bg-emerald-500/10 p-2 text-sm text-emerald-700">{aviso}</p>}

        {cobrancas.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}

        {!cobrancas.isLoading && !viva && (
          <div className="mb-4 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
            Nenhum boleto em aberto para este lançamento. Use <b>Gerar cobrança</b> na linha do
            lançamento para emitir um.
          </div>
        )}

        {viva && (
          <div className="mb-4 space-y-3 rounded-md border border-border p-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">{viva.providerName}</span>
              <span className="text-muted-foreground">· nosso número</span>
              <span className="font-mono">{viva.providerChargeId ?? '—'}</span>
              <span className={`ml-auto rounded-full px-2 py-0.5 text-xs ${STATUS_COBRANCA[viva.status].cls}`}>
                {STATUS_COBRANCA[viva.status].label}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Vencimento</div>
                {fmtData(viva.dataVencimento)}
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Valor</div>
                {formatBRL(Number(viva.valor))}
              </div>
            </div>
            {viva.linhaDigitavel && (
              <div>
                <div className="text-xs text-muted-foreground">Linha digitável</div>
                <div className="flex items-center gap-2">
                  <code className="block flex-1 break-all rounded-md bg-muted/30 p-2 text-xs">
                    {viva.linhaDigitavel}
                  </code>
                  <button
                    type="button"
                    onClick={() => void navigator.clipboard.writeText(viva.linhaDigitavel ?? '')}
                    className="rounded-md p-2 hover:bg-accent"
                    data-dica="Copiar linha digitável"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
            {situacao && (
              <p className="text-xs text-muted-foreground">
                Última consulta ao banco: <b>{situacao.status}</b>
                {situacao.paid && ' — liquidado'}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void abrirBoletoPdf(viva.id)}
                className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground hover:bg-primary/90"
              >
                <FileText className="h-4 w-4" /> Abrir PDF
              </button>
              <button
                type="button"
                disabled={ocupado}
                onClick={() => consultar.mutate(viva.id)}
                className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${consultar.isPending ? 'animate-spin' : ''}`} /> Consultar
                situação
              </button>
            </div>

            {canManage && (
              <div className="space-y-3 border-t border-border pt-3">
                <div className="flex flex-wrap items-end gap-2">
                  <label className="block space-y-1">
                    <span className="text-xs font-medium">Prorrogar vencimento para</span>
                    <input
                      type="date"
                      min={hojeIso()}
                      className="input"
                      value={novaData}
                      onChange={(e) => setNovaData(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={ocupado || !novaData}
                    onClick={() => prorrogar.mutate(viva.id)}
                    className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
                  >
                    {prorrogar.isPending ? 'Prorrogando…' : 'Prorrogar'}
                  </button>
                  <span className="text-[11px] text-muted-foreground">
                    Mesmo boleto e nosso número; só a data muda no banco.
                  </span>
                </div>

                <div className="flex flex-wrap items-end gap-2">
                  <label className="block space-y-1">
                    <span className="text-xs font-medium">Reemitir com vencimento</span>
                    <input
                      type="date"
                      min={hojeIso()}
                      className="input"
                      value={reData}
                      onChange={(e) => setReData(e.target.value)}
                    />
                  </label>
                  <label className="block space-y-1">
                    <span className="text-xs font-medium">Novo valor (opcional)</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder={String(Number(entry.valor).toFixed(2))}
                      className="input w-32"
                      value={reValor}
                      onChange={(e) => setReValor(e.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={ocupado || !reData}
                    onClick={() => {
                      if (confirm('Baixar o boleto atual no banco e emitir um novo?')) reemitir.mutate(viva.id);
                    }}
                    className="rounded-md border border-border px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
                  >
                    {reemitir.isPending ? 'Reemitindo…' : 'Reemitir'}
                  </button>
                  <span className="text-[11px] text-muted-foreground">
                    Baixa o atual e gera outro — novo nosso número e novo PDF.
                  </span>
                </div>

                <div>
                  <button
                    type="button"
                    disabled={ocupado}
                    onClick={() => {
                      if (confirm(`Baixar o boleto ${viva.providerChargeId ?? ''} no banco? Ele deixa de ser pagável.`))
                        baixar.mutate(viva.id);
                    }}
                    className="rounded-md px-3 py-2 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-50"
                  >
                    {baixar.isPending ? 'Baixando…' : 'Baixar boleto'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {cobrancas.data && cobrancas.data.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-medium">Histórico</h3>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/30 text-left">
                  <tr>
                    <th className="px-2 py-1">Quando</th>
                    <th className="px-2 py-1">Banco</th>
                    <th className="px-2 py-1">Nosso nº</th>
                    <th className="px-2 py-1">Vencimento</th>
                    <th className="px-2 py-1">Valor</th>
                    <th className="px-2 py-1">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {cobrancas.data.map((c) => (
                    <tr key={c.id} className="border-t border-border align-top">
                      <td className="px-2 py-1 whitespace-nowrap">
                        {new Date(c.createdAt).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-2 py-1">{c.providerName}</td>
                      <td className="px-2 py-1 font-mono">{c.providerChargeId ?? '—'}</td>
                      <td className="px-2 py-1">{fmtData(c.dataVencimento)}</td>
                      <td className="px-2 py-1">{formatBRL(Number(c.valor))}</td>
                      <td className="px-2 py-1">
                        <span className={`rounded-full px-2 py-0.5 ${STATUS_COBRANCA[c.status].cls}`}>
                          {STATUS_COBRANCA[c.status].label}
                        </span>
                        {c.errorMessage && (
                          <div className="mt-1 max-w-xs text-[11px] text-destructive">{c.errorMessage}</div>
                        )}
                        {c.status === 'PAID' && c.dataPagamento && (
                          <div className="mt-1 text-[11px] text-muted-foreground">
                            pago em {fmtData(c.dataPagamento)}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
